import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useOrgStore } from '@/stores/orgStore';
import { showToast } from '@/components/ui/Toast';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Switch, Button } from '@/components/ui';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { Share2, HelpCircle } from 'lucide-react';
import { t } from '@/lib/i18n';

/**
 * PlatformSharingPanel — single toggle that lets the organization opt in
 * to letting the platform super-admin view/edit their AI config, theme,
 * and organization profile. The same toggle is also exposed in the legacy
 * /organization/settings GeneralSettings page; both UIs call the same
 * mutation and read from the same org state.
 */
export function PlatformSharingPanel() {
  const { activeOrgId, orgs, fetchOrgs } = useOrgStore();
  const org = orgs.find((o: any) => o.id === activeOrgId) as any;

  const shareConfigMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await apiFetch(`/api/orgs/${encodeURIComponent(activeOrgId!)}/share-config`, {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: { message: 'Failed to update sharing preference' } }));
        throw new Error(err.error?.message || 'Failed to update sharing preference');
      }
      return res.json();
    },
    onSuccess: () => {
      fetchOrgs();
      showToast.success(t('Sharing preference updated'));
    },
    onError: (error: any) => {
      showToast.error(error.message || t('Failed to update sharing preference'));
    },
  });

  if (!activeOrgId) {
    return <div className="text-sm text-muted-foreground">No organization selected.</div>;
  }

  return (
    <div className="max-w-xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-primary-600" />
            Platform Admin Sharing
          </CardTitle>
          <CardDescription>
            Allow the platform administrator to view and help manage your organization settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">
                Share settings with platform admin
              </span>
              <SimpleTooltip
                content="When enabled, the platform administrator can view and help manage your AI configuration, theme settings, and organization profile."
                side="right"
              >
                <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground">
                  <HelpCircle className="w-4 h-4" />
                </Button>
              </SimpleTooltip>
            </div>
            <Switch
              checked={!!org?.share_config_with_admin}
              disabled={shareConfigMutation.isPending}
              onChange={(e) => shareConfigMutation.mutate(e.target.checked)}
            />
          </div>
          {org?.share_config_with_admin && (
            <p className="mt-3 text-xs text-muted-foreground">
              The platform administrator can currently view and manage your AI configuration, theme, and profile settings.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
